"use client";

import { useMemo } from "react";
import { skipToken } from "@reduxjs/toolkit/query";
import { useGetPrizeCollectionsQuery } from "@/app/redux/features/api/lifestyle";
import { buildMockCollections, USE_LIFESTYLE_MOCKS } from "@/app/redux/features/api/lifestyle.mocks";
import type { LifestyleCollectionDto, LifestylePrize } from "@/app/redux/features/api/lifestyle.types";
import { ALL_PRIZES_COLLECTION_NAME } from "../lifestyle-constants";

export interface ResolvedCollection {
  id: number;
  displayName: string;
  prizes: LifestylePrize[];
}

/** Prize plus the ITH banner title and images used by the hero. */
export type HeroPrize = LifestylePrize & {
  title: string;
  desktopImage: string;
  mobileImage: string;
  displayName: string;
};

/** Matches the design cap — more than 5 banners would hide the dots anyway. */
const MAX_HERO_SLIDES = 5;

function isCarouselCollection(collection: LifestyleCollectionDto): boolean {
  return collection.DisplayName.trim().toUpperCase() !== ALL_PRIZES_COLLECTION_NAME;
}

interface UseLifestyleCollectionsArgs {
  /** All prizes on the page, unfiltered. */
  prizes: LifestylePrize[];
  competitionId: number;
  /** True once any filter is applied; swaps the carousel rows for the flat grid. */
  isDirty: boolean;
}

/**
 * Turns the merchandised collections and ITH banners into renderable rows.
 *
 * Membership is explicit — the endpoint hands back a `PrizeIds` list per row
 * and a `PrizeId` per banner — so this is a join against the loaded prizes
 * rather than a rule engine. Ids that resolve to nothing are dropped, and
 * rows left empty are removed, so a stale collection cannot render an empty
 * carousel.
 *
 * Collections are navigation, not a filter: the modal's Collections tab
 * scrolls to a row rather than narrowing the results.
 */
export default function useLifestyleCollections({ prizes, competitionId, isDirty }: UseLifestyleCollectionsArgs) {
  const { data, isLoading } = useGetPrizeCollectionsQuery(competitionId > 0 ? { competitionId } : skipToken);

  const rawCollections = useMemo<LifestyleCollectionDto[]>(() => {
    if (USE_LIFESTYLE_MOCKS) return buildMockCollections(prizes);
    return data?.Collections ?? [];
  }, [data, prizes]);

  const prizesByContentNodeId = useMemo(() => {
    const map = new Map<number, LifestylePrize>();
    for (const prize of prizes) map.set(prize.ContentNodeId, prize);
    return map;
  }, [prizes]);

  const collections = useMemo<ResolvedCollection[]>(
    () =>
      [...rawCollections]
        .filter(isCarouselCollection)
        .sort((a, b) => a.SortOrder - b.SortOrder)
        .map(collection => ({
          id: collection.Id,
          displayName: collection.DisplayName,
          prizes: (collection.PrizeIds ?? [])
            .map(prizeId => prizesByContentNodeId.get(prizeId))
            .filter((prize): prize is LifestylePrize => prize !== undefined),
        }))
        .filter(collection => collection.prizes.length > 0),
    [rawCollections, prizesByContentNodeId]
  );

  const heroPrizes = useMemo<HeroPrize[]>(() => {
    if (USE_LIFESTYLE_MOCKS) return [];

    return (data?.Banners ?? [])
      .map(banner => {
        const prize = prizesByContentNodeId.get(banner.PrizeId);
        if (!prize) return null;

        const desktopImage = banner.DesktopImage || banner.MobileImage;
        const mobileImage = banner.MobileImage || banner.DesktopImage;
        if (!desktopImage && !mobileImage) return null;

        return {
          ...prize,
          title: banner.Title ?? "",
          desktopImage,
          mobileImage,
          displayName:
            collections.find(collection =>
              collection.prizes.some(member => member.ContentNodeId === prize.ContentNodeId)
            )?.displayName ?? "",
        };
      })
      .filter((prize): prize is HeroPrize => prize !== null)
      .slice(0, MAX_HERO_SLIDES);
  }, [collections, data, prizesByContentNodeId]);

  return {
    collections,
    heroPrizes,
    /** When true the page renders the flat ALL PRIZES grid instead of the rows. */
    isFiltering: isDirty,
    isLoading: USE_LIFESTYLE_MOCKS ? false : isLoading,
  };
}
